import type { useLocation } from 'react-router';

import type { OnAcceptConsentResponse, OnDenyConsentResponse } from './types';

const SESSION_ID_PARAM = 'session_id';
const STATE_PARAM = 'state';
export const FORCE_LOGIN = 'force_login';
const LOGIN_HINT = 'login_hint';
export const AUTHORIZATION_DETAILS = 'authorization_details';
const INSTANCE_UID = 'instance_uid';
export const INSTANCE_NAME = 'instance_name';
export const BACK_TO = 'back_to';

export interface OAuthParams {
  sessionId: string;
  state: string | null;
  loginHint: string | null;
  instanceUID: string | null;
}

const getAuthorizationDetail = (authorizationDetails: string | null, type: string): string | null => {
  if (!authorizationDetails) {
    return null;
  }

  try {
    const details = JSON.parse(authorizationDetails);
    const detail = details.find((d: { identifier: string; type: string }) => d.type === type);

    return detail ? detail.identifier : null;
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: we don't have a logger here so we use console.error
    console.error('Failed to parse authorization details:', error);
    return null;
  }
};

export function getOAuthParams(location: ReturnType<typeof useLocation>): OAuthParams {
  const sp = new URLSearchParams(location.search);
  const authorizationDetails = sp.get(AUTHORIZATION_DETAILS) ?? null;
  const instanceUID = getAuthorizationDetail(authorizationDetails, INSTANCE_UID);
  const loginHint = sp.get(LOGIN_HINT) ?? null;

  return {
    sessionId: sp.get(SESSION_ID_PARAM) as string,
    state: sp.get(STATE_PARAM),
    loginHint: loginHint ? decodeURIComponent(loginHint) : null,
    instanceUID,
  };
}

export function getOAuthRedirectURL(response: OnAcceptConsentResponse | OnDenyConsentResponse) {
  if (!response.redirect_uri) {
    throw new Error("Response doesn't have `redirect_uri` parameter");
  }
  if (!response.state) {
    throw new Error("Response doesn't have `state` parameter");
  }
  const redirectURL = new URL(response.redirect_uri);

  redirectURL.searchParams.set('state', response.state);

  if ('error' in response) {
    redirectURL.searchParams.set('error', response.error);
    if (response.error_description) {
      redirectURL.searchParams.set('error_description', response.error_description);
    }
    return redirectURL.toString();
  }

  redirectURL.searchParams.set('code', response.code);
  return redirectURL.toString();
}

export function isOAuthQueryValid(location: ReturnType<typeof useLocation>) {
  const sp = new URLSearchParams(location.search);

  return [SESSION_ID_PARAM]
    .map(paramName => {
      const params = sp.getAll(paramName);
      if (params.length !== 1) {
        return false;
      }
      const [param] = params;
      return param.length !== 0;
    })
    .every(Boolean);
}

export function setSearchParam(
  location: ReturnType<typeof useLocation>,
  value: {
    forceLogin: boolean;
    instanceUID?: string;
  }
) {
  const sp = new URLSearchParams(location.search);

  if (value.forceLogin) {
    sp.set(FORCE_LOGIN, 'true');
  } else {
    sp.delete(FORCE_LOGIN);
  }

  if (value.instanceUID) {
    sp.set(INSTANCE_UID, value.instanceUID);
  } else {
    sp.delete(INSTANCE_UID);
  }

  return `?${sp.toString()}`;
}

export function hasForceLogin(location: ReturnType<typeof useLocation>) {
  const sp = new URLSearchParams(location.search);

  return !!sp.get(FORCE_LOGIN) && sp.getAll(FORCE_LOGIN).reverse()[0] === 'true';
}

export function hasSessionId(location: ReturnType<typeof useLocation>) {
  const sp = new URLSearchParams(location.search);
  return sp.has(SESSION_ID_PARAM);
}
